/**
 * Guide bulk add-to-cart — "Alle Teile in den Warenkorb".
 *
 * Progressive enhancement for the guide parts box (markup-contract.md §Guide
 * parts box): the `data-datte-guide-bulk-cart` button ships DISABLED. This
 * module enables it and, on click, adds every article listed in
 * `data-article-ids` to the JTL cart, showing live progress and a visible
 * success/error end state.
 *
 * HOW (root-caused on staging, 2026-07-04): the add goes through the shop's own
 * IO route — a POST to `<jtl-io-path>/io` with body `io={"name":"pushToBasket",
 * "params":[kArtikel, 1, {jtl_token}]}` and header `X-Requested-With:
 * XMLHttpRequest`. This is the SAME endpoint + payload the shop uses everywhere;
 * we send the REAL session CSRF token read live from the shop-wide `.jtl_token`
 * input (guide pages are served uncached — `x-litespeed-cache: miss` — so that
 * token is fresh and valid). No CSRF bypass, no token-less write endpoint.
 *
 * Why a direct `fetch` and NOT `$.evo.io()`: guide pages don't load the classic
 * `jtl.io.js`; `$.evo.io` is hooked by the kk_dropper DropperFramework, whose
 * `io` POSTs to `window.location.href` (the cached guide URL) → 403. Calling the
 * `/io` route directly sidesteps that and is verified working (nType 2).
 */

const SECTION_SELECTOR = '[data-datte-guide-parts]';
const BUTTON_SELECTOR = 'button[data-datte-guide-bulk-cart]';
const TOKEN_SELECTOR = 'input[name="jtl_token"], .jtl_token';
const IO_PATH_SELECTOR = '#jtl-io-path';
const CART_LINK_SELECTOR = '.basket-opener, a[href*="Warenkorb"], a[href*="warenkorb"]';

const LABEL_ERROR = 'Konnte nicht hinzugefügt werden';
const TITLE_ERROR =
  'Die Teile konnten nicht automatisch in den Warenkorb gelegt werden. Bitte einzeln über ihre Produktseiten bestellen.';

/** pushToBasket via /io returns `varAssigns: [{name:'response', value:{nType}}]`; nType 2 = added. */
interface IoEnvelope {
  varAssigns?: Array<{ name: string; value?: { nType?: number } }>;
}

type AddOutcome = 'added' | 'skipped';

function readToken(root: ParentNode): string | null {
  // NOT simply the first match: with an admin session, admin fragments (e.g.
  // the OPC editor toolbar) embed ADMIN-session tokens ahead of the frontend
  // forms, and /io rejects those with "Fehlerhafter Token" (staging P0,
  // 2026-07-04). Only tokens outside /admin forms are valid for pushToBasket.
  // getAttribute, NOT the .action property: the OPC forms contain a button
  // named "action" that clobbers the property with the element itself.
  for (const input of Array.from(root.querySelectorAll<HTMLInputElement>(TOKEN_SELECTOR))) {
    if (input.closest('form')?.getAttribute('action')?.includes('/admin')) {
      continue;
    }
    const value = input.value?.trim();
    if (value) {
      return value;
    }
  }
  return null;
}

/** IO base path from the shop's `#jtl-io-path` element; '' = same origin. */
function readIoPath(root: ParentNode): string {
  return root.querySelector<HTMLElement>(IO_PATH_SELECTOR)?.dataset.path ?? '';
}

/** Cart page URL from a header basket link, else the SEO fallback. */
function readCartUrl(root: ParentNode, ioPath: string): string {
  const href = root.querySelector<HTMLAnchorElement>(CART_LINK_SELECTOR)?.href;
  return href || `${ioPath}/Warenkorb`;
}

/** Unique positive ints from the semicolon list, in picked order. */
function parseArticleIds(raw: string | null | undefined): number[] {
  if (!raw) {
    return [];
  }
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const part of raw.split(';')) {
    const id = Number.parseInt(part.trim(), 10);
    if (Number.isInteger(id) && id > 0 && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Add ONE article via the shop's /io pushToBasket. 'added' only when the shop
 * confirms nType===2; a non-2xx (e.g. 403), a thrown fetch, or any other nType
 * counts as 'skipped'.
 */
async function addOne(ioUrl: string, articleId: number, token: string): Promise<AddOutcome> {
  const req = { name: 'pushToBasket', params: [articleId, 1, { jtl_token: token }] };
  try {
    const res = await fetch(ioUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: 'io=' + encodeURIComponent(JSON.stringify(req)),
    });
    if (!res.ok) {
      return 'skipped';
    }
    const data = (await res.json()) as IoEnvelope;
    const nType = data.varAssigns?.find((v) => v.name === 'response')?.value?.nType;
    return nType === 2 ? 'added' : 'skipped';
  } catch {
    return 'skipped';
  }
}

/** Put the button into its visible, disabled error end state. */
function showError(button: HTMLButtonElement): void {
  button.removeAttribute('aria-busy');
  button.textContent = LABEL_ERROR;
  button.title = TITLE_ERROR;
  button.disabled = true; // solid-grey disabled style (AP1) — a VISIBLE failure
}

/** Isolated for testability. */
function navigateToCart(cartUrl: string): void {
  window.location.assign(cartUrl);
}

/**
 * Run the bulk add for one parts box: disable, add each id with a live progress
 * label, then settle into a visible success (→ cart) or error state.
 */
async function runBulkAdd(
  button: HTMLButtonElement,
  ids: number[],
  token: string,
  ioUrl: string,
  cartUrl: string,
): Promise<void> {
  const total = ids.length;
  let added = 0;
  let done = 0;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = `Lege Teil 1/${total} in den Warenkorb …`;

  for (const id of ids) {
    button.textContent = `Lege Teil ${done + 1}/${total} in den Warenkorb …`;
    // sequential by design: the shop mutates one shared cart, parallel adds race.
    const outcome = await addOne(ioUrl, id, token);
    if (outcome === 'added') {
      added += 1;
    }
    done += 1;
  }

  button.removeAttribute('aria-busy');

  if (added === 0) {
    showError(button);
    return;
  }

  // Success (full or partial): show the count, then hand off to the cart page —
  // the honest confirmation + it refreshes the header cart counter.
  button.textContent =
    added < total ? `${added} von ${total} Teilen hinzugefügt` : 'Alle Teile hinzugefügt';
  navigateToCart(cartUrl);
}

/**
 * Wire one bulk-cart button: enable it (progressive enhancement) and bind the
 * click. Leaves it disabled when there is nothing to add or no token.
 */
function wireButton(button: HTMLButtonElement): void {
  const section = button.closest<HTMLElement>(SECTION_SELECTOR);
  const ids = parseArticleIds(section?.dataset.articleIds);
  const doc = button.ownerDocument;

  if (ids.length === 0 || readToken(doc) === null) {
    return; // keep the disabled resting state + its title hint
  }

  button.disabled = false;
  button.removeAttribute('title');

  button.addEventListener('click', () => {
    const token = readToken(doc);
    if (token === null) {
      showError(button);
      return;
    }
    const ioPath = readIoPath(doc);
    void runBulkAdd(button, ids, token, `${ioPath}/io`, readCartUrl(doc, ioPath));
  });
}

/**
 * Initialise every guide bulk-cart button under `root`. Safe to call on any
 * page — a no-op where no button exists.
 */
export function initGuideBulkCart(root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>(BUTTON_SELECTOR).forEach(wireButton);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGuideBulkCart());
  } else {
    initGuideBulkCart();
  }
}
